import { Chip } from '@heroui/react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { RefObject, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionCableContext } from '@/context/ActionCableContext';
import { useReadSecureMessageThreadMutation } from '@/patient-app-common/api/secureMessageApi';
import { MessagePayload } from '@/patient-app-common/types/SecureMessageThreads';

import { ImageThumbnail } from './ImagePreview';
import { PdfPreview } from './PdfPreview';

interface ThreadProps {
  id: number | null;
  threadRef: RefObject<HTMLDivElement | null>;
  replyMode: boolean;
}

interface GroupedMessages {
  [date: string]: MessagePayload[];
}

export function Thread({ id, threadRef, replyMode }: ThreadProps) {
  const { t } = useTranslation('inbox');
  const bottomRef = useRef<HTMLDivElement>(null);

  const [readThread] = useReadSecureMessageThreadMutation();

  const { secureMessageThreads, markThreadAsRead } =
    useContext(ActionCableContext);

  const openedThread = useMemo(() => {
    if (!secureMessageThreads.length) return null;
    const thread = _.find(secureMessageThreads, ['id', id]);

    return thread;
  }, [secureMessageThreads, id]);

  useEffect(() => {
    if (!openedThread || !openedThread?.unreadMessage) return;

    const read = async () => {
      try {
        const result = await readThread(openedThread.id).unwrap();

        if (result.success) {
          markThreadAsRead(openedThread.id);
        }
      } catch (e) {
        console.log(e);
      }
    };

    read();
  }, [openedThread]);

  useEffect(() => {
    if (bottomRef.current && threadRef.current) {
      const anchorEl = bottomRef.current;
      const containerEl = threadRef.current;

      containerEl.scrollTo({
        top: anchorEl.offsetTop,
        behavior: 'instant',
      });
    }
  }, [id, replyMode]);

  const groupedMessages = useMemo(() => {
    if (!openedThread?.messages) return [];

    const messages: GroupedMessages[] = [];

    openedThread.messages.forEach((message) => {
      let date = dayjs(message.createdAt).format('MMM D, YYYY');

      const isCurrentYear = dayjs(message.createdAt).isSame(dayjs(), 'year');
      if (isCurrentYear) {
        date = dayjs(message.createdAt).format('MMM D');
      }

      const dateGroup = _.find(messages, date);

      if (!dateGroup) {
        messages.push({ [date]: [message] });
      } else {
        dateGroup[date].push(message);
      }
    });

    return messages;
  }, [openedThread]);

  return (
    <div
      className="flex-1 overflow-auto focus:outline-none"
      ref={threadRef}
      tabIndex={0}
    >
      <div className="w-full md:w-3/4 m-auto">
        {groupedMessages.map((group) => {
          const [date, messages] = Object.entries(group)[0];

          return (
            <div className="m-4" key={date}>
              <div className="flex justify-center my-4">
                <Chip color="primary" variant="faded">
                  {date}
                </Chip>
              </div>
              {messages.map((message) => (
                <div className="mb-4" key={message.id}>
                  <div className="inline-flex">
                    <p className="font-semibold">{message.sentByName}</p>
                    <p className="font-light ml-4">
                      {dayjs(message.createdAt).format('hh:mm a')}
                    </p>
                  </div>
                  <p>{message.text}</p>
                  {message.file && message.fileType?.includes('image') && (
                    <>
                      <p className="text-default-500">
                        {t('common.text.attachment')}
                      </p>
                      <ImageThumbnail
                        src={message.file}
                        width={300}
                        height={300}
                      />
                    </>
                  )}
                  {message.file && message.fileType?.includes('pdf') && (
                    <>
                      <p className="text-default-500">
                        {t('common.text.attachment')}
                      </p>
                      <PdfPreview file={message.file} />
                    </>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div ref={bottomRef} className="float-left clear-both" />
    </div>
  );
}
